import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../app/auth';
import { Button, Input } from '../../../components/ui';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { UserRole } from '../../../types';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuth();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sesionActivaDetectada, setSesionActivaDetectada] = useState(false);
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    const msg = sessionStorage.getItem('tiempos_login_aviso');
    if (msg) {
      setAviso(msg);
      sessionStorage.removeItem('tiempos_login_aviso');
    }
  }, []);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;

  const getRoleRedirect = (role: UserRole): string => {
    const redirects: Record<UserRole, string> = {
      ADMIN: '/admin',
      BANCA: '/banca',
      LOCAL: '/local',
      VENDEDOR_CON_CAJA: '/vendedor/pos',
      VENDEDOR_SIN_CAJA: '/vendedor/pos',
    };
    return redirects[role];
  };

  // Valida que el "from" guardado sea accesible para el rol actual
  const getValidatedRedirect = (from: string | undefined, role: UserRole): string => {
    const roleAllowedPrefixes: Record<UserRole, string[]> = {
      ADMIN: ['/admin'],
      BANCA: ['/banca'],
      LOCAL: ['/local', '/vendedor/creditos'],
      VENDEDOR_CON_CAJA: ['/vendedor', '/loteria-caja'],
      VENDEDOR_SIN_CAJA: ['/vendedor'],
    };
    const allowed = roleAllowedPrefixes[role] ?? [];
    if (from && allowed.some(prefix => from.startsWith(prefix))) {
      return from;
    }
    return getRoleRedirect(role);
  };

  const handleSubmit = async (e: React.FormEvent, forzar = false) => {
    e.preventDefault();
    setError('');
    setSesionActivaDetectada(false);
    setIsSubmitting(true);

    try {
      const result = await login({ username, password }, forzar);

      if (result.sesionActiva) {
        setSesionActivaDetectada(true);
        return;
      }

      if (result.ok) {
        // Obtener el usuario para saber a dónde redirigir
        const stored = localStorage.getItem('tiempos_auth_user');
        if (stored) {
          const user = JSON.parse(stored);
          const redirect = getValidatedRedirect(from, user.role);
          navigate(redirect, { replace: true });
        }
      } else {
        setError('Usuario o contraseña incorrectos');
      }
    } catch {
      setError('Error al iniciar sesión');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8">
      {/* Logo */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
          <span className="text-3xl font-bold text-white">T</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">TiemPos v2</h1>
        <p className="text-gray-500 mt-1">Sistema de Lotería</p>
      </div>

      {/* Form */}
      {aviso && (
        <div className="bg-blue-50 border border-blue-300 text-blue-800 px-4 py-3 rounded-lg text-sm">
          {aviso}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <Input
          label="Usuario"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Ingrese su usuario"
          leftIcon={<User size={18} />}
          autoComplete="username"
          required
        />

        <div className="relative">
          <Input
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Ingrese su contraseña"
            leftIcon={<Lock size={18} />}
            autoComplete="current-password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {error && (
          <div className="bg-danger-50 text-danger-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {sesionActivaDetectada && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-3">
            <p className="text-amber-800 font-medium text-sm">
              ⚠️ Este usuario ya tiene una sesión activa en otro dispositivo.
            </p>
            <p className="text-amber-700 text-xs">
              Si usted no reconoce esta sesión activa, se recomienda cambiar su contraseña por seguridad.
            </p>
            <button
              type="button"
              onClick={(e) => handleSubmit(e as any, true)}
              disabled={isSubmitting}
              className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              Forzar cierre de sesión anterior e ingresar
            </button>
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={isSubmitting || isLoading}
        >
          Iniciar Sesión
        </Button>
      </form>
    </div>
  );
};

export default LoginPage;
